import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Loader2 } from 'lucide-react';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { getEpisodeVitalsSummary } from '../../services/api';
import type {
  EpisodeVisitVitalsReading,
  EpisodeVitalsOrderedParameters,
  EpisodeVitalsSummaryResponse,
  VitalReadingValue,
} from '../../services/api/types.gen';
import { getDisciplineForVisitType } from '../../constants/visitTypes';
import type { DisciplineType } from '../../services/api/models/DisciplineType';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { patientVisitPath } from '../../utils/navigation';
import { EpisodeVitalsCharts } from './EpisodeVitalsCharts';
import {
  VITAL_COLUMNS,
  formatVitalNumber,
  getOrderedRange,
  getStat,
  isOutsideOrderedRange,
  summarizeReadings,
  type VitalKey,
} from './vitalsConfig';

type EpisodeVitalsSummaryProps = {
  patientId: string;
  episodeId: string | null | undefined;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
};

const DISCIPLINE_OPTIONS: Array<{
  value: DisciplineType | 'all';
  label: string;
}> = [
  { value: 'all', label: 'All disciplines' },
  { value: 'SN', label: 'SN' },
  { value: 'PT', label: 'PT' },
  { value: 'OT', label: 'OT' },
  { value: 'ST', label: 'ST' },
  { value: 'MSW', label: 'MSW' },
  { value: 'HHA', label: 'HHA' },
];

function displayVital(
  reading: EpisodeVisitVitalsReading,
  key: VitalKey
): string {
  const vital = reading.vitals[key];
  if (!vital) return '—';
  let base =
    vital.display != null && vital.display !== ''
      ? vital.display
      : formatVitalNumber(vital.value);
  if (vital.context) {
    base = `${base} (${vital.context})`;
  }
  if (vital.scale && key === 'pain') {
    base = `${base} · ${vital.scale}`;
  }
  return base;
}

function columnLabel(
  key: VitalKey,
  baseLabel: string,
  readings: EpisodeVisitVitalsReading[]
): string {
  if (key !== 'pain') return baseLabel;
  const scale = readings
    .map(r => r.vitals.pain?.scale)
    .find(s => s != null && s !== '');
  return scale ? `${baseLabel} (${scale})` : baseLabel;
}

function vitalCellClass(
  vital: VitalReadingValue | null | undefined,
  key: VitalKey,
  params: EpisodeVitalsOrderedParameters | undefined
): string {
  const range = getOrderedRange(params, key, vital?.context);
  if (isOutsideOrderedRange(vital?.value, range)) {
    return 'px-3 py-2 tabular-nums font-medium text-destructive';
  }
  return 'px-3 py-2 tabular-nums';
}

/** Latest reading for a vital in chronological order (for context / range). */
function latestReadingForKey(
  readings: EpisodeVisitVitalsReading[],
  key: VitalKey
): EpisodeVisitVitalsReading | null {
  let latest: EpisodeVisitVitalsReading | null = null;
  const sorted = [...readings].sort((a, b) => {
    const byDate = a.visit_date.localeCompare(b.visit_date);
    if (byDate !== 0) return byDate;
    const aSec = a.visit_start_seconds ?? -1;
    const bSec = b.visit_start_seconds ?? -1;
    if (aSec !== bSec) return aSec - bSec;
    return a.visit_id.localeCompare(b.visit_id);
  });
  for (const reading of sorted) {
    const vital = reading.vitals[key];
    if (vital?.value == null || Number.isNaN(vital.value)) continue;
    latest = reading;
  }
  return latest;
}

function filterReadings(
  readings: EpisodeVisitVitalsReading[],
  dateFrom: string,
  dateTo: string,
  discipline: DisciplineType | 'all'
): EpisodeVisitVitalsReading[] {
  return readings.filter(reading => {
    if (dateFrom && reading.visit_date < dateFrom) return false;
    if (dateTo && reading.visit_date > dateTo) return false;
    if (discipline !== 'all') {
      const d = getDisciplineForVisitType(reading.visit_type);
      if (d !== discipline) return false;
    }
    return true;
  });
}

export function EpisodeVitalsSummary({
  patientId,
  episodeId,
  episodeStartDate,
  episodeEndDate,
}: EpisodeVitalsSummaryProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<EpisodeVitalsSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [discipline, setDiscipline] = useState<DisciplineType | 'all'>('all');

  useEffect(() => {
    if (!episodeId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setDateFrom('');
    setDateTo('');
    setDiscipline('all');

    void (async () => {
      try {
        const resp = await getEpisodeVitalsSummary({
          path: { patient_id: patientId, episode_id: episodeId },
          throwOnError: true,
        });
        if (cancelled) return;
        setData(resp.data ?? null);
      } catch {
        if (cancelled) return;
        setData(null);
        setError('Unable to load episode vitals.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientId, episodeId]);

  const readings = data?.readings ?? [];
  const filteredReadings = useMemo(
    () => filterReadings(readings, dateFrom, dateTo, discipline),
    [readings, dateFrom, dateTo, discipline]
  );
  // Recompute from the visible set so filters don't leave episode-wide Min/Max/Latest.
  const summary = useMemo(
    () => summarizeReadings(filteredReadings),
    [filteredReadings]
  );
  const orderedParameters = data?.ordered_parameters;
  const start = data?.episode_start_date ?? episodeStartDate;
  const end = data?.episode_end_date ?? episodeEndDate;
  const filtersActive =
    Boolean(dateFrom) || Boolean(dateTo) || discipline !== 'all';

  if (!episodeId) {
    return (
      <div className='rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground'>
        Select an episode to view vital signs across completed visits.
      </div>
    );
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading vitals…
      </div>
    );
  }

  if (error) {
    return (
      <div className='rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive'>
        {error}
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <header className='flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between'>
        <div>
          <div className='flex items-center gap-2'>
            <Activity className='h-4 w-4 text-muted-foreground' />
            <h2 className='text-base font-semibold'>Vital Signs</h2>
          </div>
          <p className='text-sm text-muted-foreground'>
            Episode {formatDateToMMDDYYYY(start) || '—'} –{' '}
            {formatDateToMMDDYYYY(end) || '—'}. Completed visits only.
          </p>
        </div>
        <p className='text-xs text-muted-foreground'>
          {filteredReadings.length}
          {filtersActive ? ` of ${readings.length}` : ''} visit
          {filteredReadings.length === 1 ? '' : 's'} with vitals
        </p>
      </header>

      {readings.length === 0 ? (
        <div className='rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground'>
          No completed visits in this episode have documented vital signs yet.
        </div>
      ) : (
        <>
          <section
            aria-label='Vitals filters'
            className='flex flex-wrap items-end gap-3'
          >
            <label className='flex flex-col gap-1 text-xs text-muted-foreground'>
              From
              <Input
                type='date'
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                inputSize='sm'
                className='w-full md:w-40'
              />
            </label>
            <label className='flex flex-col gap-1 text-xs text-muted-foreground'>
              To
              <Input
                type='date'
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                inputSize='sm'
                className='w-full md:w-40'
              />
            </label>
            <label className='flex flex-col gap-1 text-xs text-muted-foreground'>
              Discipline
              <Select
                value={discipline}
                onValueChange={v => setDiscipline(v as DisciplineType | 'all')}
              >
                <SelectTrigger selectSize='sm' className='w-full md:w-44'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DISCIPLINE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </section>

          {filteredReadings.length === 0 ? (
            <div className='rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground'>
              No visits match the current filters.
            </div>
          ) : (
            <>
              <section aria-label='Vitals summary'>
                <h3 className='mb-2 text-sm font-semibold'>Summary</h3>
                <div className='overflow-x-auto rounded-md border'>
                  <table className='w-full min-w-[640px] text-left text-sm'>
                    <thead className='bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground'>
                      <tr>
                        <th className='px-3 py-2 font-medium'> </th>
                        {VITAL_COLUMNS.map(col => (
                          <th key={col.key} className='px-3 py-2 font-medium'>
                            {columnLabel(col.key, col.label, filteredReadings)}
                            {getStat(summary, col.key).unit
                              ? ` (${getStat(summary, col.key).unit})`
                              : ''}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(
                        [
                          ['Min', 'min'],
                          ['Max', 'max'],
                          ['Latest', 'latest'],
                        ] as const
                      ).map(([label, field]) => (
                        <tr key={label} className='border-t'>
                          <th className='px-3 py-2 text-left font-medium text-muted-foreground'>
                            {label}
                          </th>
                          {VITAL_COLUMNS.map(col => {
                            const stat = getStat(summary, col.key);
                            const value =
                              field === 'latest' && stat.latest_display
                                ? stat.latest_display
                                : formatVitalNumber(stat[field]);
                            const latestDate =
                              field === 'latest' && stat.latest_visit_date
                                ? formatDateToMMDDYYYY(
                                    stat.latest_visit_date
                                  ) || '—'
                                : null;
                            const latestContext =
                              field === 'latest'
                                ? latestReadingForKey(filteredReadings, col.key)
                                    ?.vitals[col.key]?.context
                                : null;
                            const outOfRange =
                              field === 'latest' &&
                              isOutsideOrderedRange(
                                stat.latest,
                                getOrderedRange(
                                  orderedParameters,
                                  col.key,
                                  latestContext
                                )
                              );
                            return (
                              <td
                                key={col.key}
                                className={
                                  outOfRange
                                    ? 'px-3 py-2 tabular-nums font-medium text-destructive'
                                    : 'px-3 py-2 tabular-nums'
                                }
                              >
                                <div>{value}</div>
                                {latestDate && value !== '—' && (
                                  <div className='text-[11px] font-normal text-muted-foreground'>
                                    {latestDate}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {orderedParameters &&
                  Object.values(orderedParameters).some(Boolean) && (
                    <p className='mt-1.5 text-xs text-muted-foreground'>
                      Values outside ordered parameters are highlighted.
                    </p>
                  )}
              </section>

              <EpisodeVitalsCharts readings={filteredReadings} />

              <section aria-label='Per-visit vitals'>
                <h3 className='mb-2 text-sm font-semibold'>
                  Per-visit readings
                </h3>
                <div className='overflow-x-auto rounded-md border'>
                  <table className='w-full min-w-[900px] text-left text-sm'>
                    <thead className='bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground'>
                      <tr>
                        <th className='px-3 py-2 font-medium'>Date</th>
                        <th className='px-3 py-2 font-medium'>Visit</th>
                        <th className='px-3 py-2 font-medium'>Clinician</th>
                        {VITAL_COLUMNS.map(col => (
                          <th key={col.key} className='px-3 py-2 font-medium'>
                            {columnLabel(col.key, col.label, filteredReadings)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...filteredReadings].reverse().map(reading => (
                        <tr
                          key={reading.visit_id}
                          className='cursor-pointer border-t hover:bg-muted/30'
                          onClick={() =>
                            navigate(
                              patientVisitPath(
                                patientId,
                                reading.visit_id,
                                episodeId
                              )
                            )
                          }
                        >
                          <td className='px-3 py-2 whitespace-nowrap'>
                            {formatDateToMMDDYYYY(reading.visit_date) || '—'}
                          </td>
                          <td className='px-3 py-2'>
                            {reading.visit_type_display_name}
                          </td>
                          <td className='px-3 py-2'>
                            {reading.clinician_name ?? '—'}
                          </td>
                          {VITAL_COLUMNS.map(col => (
                            <td
                              key={col.key}
                              className={vitalCellClass(
                                reading.vitals[col.key],
                                col.key,
                                orderedParameters
                              )}
                            >
                              {displayVital(reading, col.key)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
}
